import { Box, Divider } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActionsMenu,
  ActiveContainer,
  HeaderWrapper,
  HelperText,
  ImagePreview,
  LeftContent,
  ProfileImageBox,
  SectionTitle,
  StudentProfileWrapper,
  TitleBorderWrapper,
  UploadButton
} from '../styled';
import { Control, UseFormSetValue } from 'react-hook-form';
import { StyledButton } from '../../Exams/styled';
import { studentProfileSchema } from '../config/schema';
import Icons from '../../../components/icons';
import { StudentDetailProps } from '../types/studentDetailType';
import FormRenderer from '../../../components/FormRenderer/FormRenderer';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import studentProfile from '../../../apis/studentProfile';
import {
  StudentActionItems,
  ConfirmationDialogMessages
} from '../config/constant';
import { SnackbarSeverityType, Status } from '../../../enums';
import DialogModal from '../../../components/DialogModal';
import GenerateLcForm from './GenerateLcForm';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { useStudentActions } from '../hooks/useStudentActions';
import pallet from '../../../layouts/pallet';
import { transformStudentDetailData } from '../../../utils/studentDataTransformer';
import { superAdminUsers } from '../../../constants/superAdminUsers';
import CustomMenuItem from '../../../components/CustomMenuItem/CustomMenuItem';

const StudentProfile = ({
  control,
  setValue,
  showToast
}: {
  control: Control<StudentDetailProps, any, StudentDetailProps>;
  setValue: UseFormSetValue<StudentDetailProps>;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}) => {
  const studentDetails = control._getWatch('studentDetails');
  const reset = control._reset;
  const [lcRequestData, setLcRequestData] = useState<any>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isGenerateLcModalOpen, setIsGenerateLcModalOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationDialogMessage, setConfirmationDialogMessage] =
    useState('');
  const [actionToConfirm, setActionToConfirm] = useState('');
  const [isBonafideLoading, setIsBonafideLoading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const { schoolConfig } = useSelector(
    (state: RootState) => state.schoolConfig
  );

  const [actionsAnchorEl, setActionsAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const isActionsOpen = Boolean(actionsAnchorEl);

  const handleOpenGenerateLcModal = () => setIsGenerateLcModalOpen(true);
  const handleCloseGenerateLcModal = () => setIsGenerateLcModalOpen(false);

  const {
    uploadStudentPhoto,
    acceptRejectLCRequest,
    generateBonafide,
    activateStudent,
    deActivateStudent,
    generateLC,
    requestLC
  } = useStudentActions(
    studentDetails?.id,
    setValue,
    showToast,
    handleCloseGenerateLcModal
  );

  const handleOpenActions = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };
  const handleCloseActions = () => setActionsAnchorEl(null);

  const handleOpenConfirmationDialog = (message: string, action: string) => {
    setConfirmationDialogMessage(message);
    setActionToConfirm(action);
    setConfirmationDialogOpen(true);
  };

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
    setConfirmationDialogMessage('');
    setActionToConfirm('');
  };

  const getStudentDetail = useCallback(async () => {
    if (!studentDetails.id) return;
    try {
      const params = {
        studentIds: [studentDetails.id]
      };
      const response = await studentProfile.fetchStudentDetailApi(params);

      const transformedStudentDetailData = transformStudentDetailData(response);
      reset(transformedStudentDetailData as any);
    } catch (error: any) {
      showToast(
        error.errors.message || 'Error getting student detail',
        SnackbarSeverityType.error
      );
    }
  }, [showToast, reset, studentDetails?.id]);

  const fetchLCRequest = async () => {
    if (!studentDetails?.id) return;
    try {
      const response = await studentProfile.fetchStudentLCRequest(
        studentDetails.id
      );
      setLcRequestData(response);
    } catch (error) {
      setLcRequestData(null);
    }
  };

  const handleConfirmAction = async () => {
    handleCloseConfirmationDialog();
    handleCloseActions();

    const studentActionFunctionMap: Record<string, () => Promise<void>> = {
      reject_lc: () => acceptRejectLCRequest('REJECTED'),
      accept_lc: () => acceptRejectLCRequest('ACCEPTED'),
      generate_bonafide: async () => {
        setIsBonafideLoading(true);
        try {
          await generateBonafide();
        } finally {
          setIsBonafideLoading(false);
        }
      },
      activate_student: activateStudent,
      deactivate_student: deActivateStudent,
      request_lc: requestLC
    };

    const studentActionFunction = studentActionFunctionMap[actionToConfirm];
    if (studentActionFunction) {
      await studentActionFunction();
      await getStudentDetail();
      if (
        actionToConfirm === 'reject_lc' ||
        actionToConfirm === 'accept_lc' ||
        actionToConfirm === 'request_lc'
      ) {
        await fetchLCRequest();
      }
    }
  };

  useEffect(() => {
    fetchLCRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentDetails?.id]);

  // const lcStatus = lcRequestData?.data?.status;
  const studentStatus = studentDetails?.status;

  const isSuperAdmin = superAdminUsers?.includes(loggedInUser.email);

  const visibleActionItems = StudentActionItems.filter((item) => {
    const { key } = item;
    // const isLcAcceptedAndInactive =
    //   lcStatus === 'ACCEPTED' && studentStatus === Status.INACTIVE;

    const isBonafideEnabled =
      schoolConfig?.featureConfigs?.bonafide?.enabled ?? false;

    const conditionToShowStudentActionItem: Record<string, boolean> = {
      reject_lc: false, // lcStatus === 'IN_APPROVAL'
      accept_lc: false, // lcStatus === 'IN_APPROVAL'
      activate_student: studentStatus !== Status.ACTIVE && isSuperAdmin,
      deactivate_student: studentStatus !== Status.INACTIVE && isSuperAdmin,
      generate_bonafide: isBonafideEnabled,
      generate_lc: false, //isLcAcceptedAndInactive,
      request_lc: false // isUndefined(lcStatus) || lcStatus === 'REJECTED'
    };

    return conditionToShowStudentActionItem[key] ?? true;
  });

  const handleActionClick = async (actionKeyName: string) => {
    if (actionKeyName === 'generate_lc') {
      handleOpenGenerateLcModal();
      handleCloseActions();
      return;
    }

    const message =
      ConfirmationDialogMessages[actionKeyName] ||
      ConfirmationDialogMessages.default;
    handleOpenConfirmationDialog(message, actionKeyName);
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0] || null;
    await uploadStudentPhoto(file);
    setFileInputKey((prev) => prev + 1);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const rollOverInfo = studentDetails?.rollOverInfo;

  const isNewAdmissionStudent =
    rollOverInfo &&
    rollOverInfo.length === 1 &&
    rollOverInfo?.[0].isNewAdmission === true
      ? true
      : false;

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <TitleBorderWrapper>
        <SectionTitle>Student Profile Details</SectionTitle>
        <Divider sx={{ width: '100%' }} />
      </TitleBorderWrapper>
      <StudentProfileWrapper>
        <ProfileImageBox>
          <ImagePreview src={studentDetails?.profile || ''} alt='Profile' />

          <input
            key={fileInputKey}
            ref={fileInputRef}
            type='file'
            style={{ display: 'none' }}
            accept='.jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif'
            onChange={handleFileInputChange}
          />

          <UploadButton
            fullWidth
            startIcon={<Icons.ImageFillPrimary />}
            onClick={handleButtonClick}
          >
            Upload Photo
          </UploadButton>
          <HelperText>Allowed JPG, GIF or PNG. Max size of 800K</HelperText>
        </ProfileImageBox>
        <Box flex={1} width='100%'>
          <HeaderWrapper>
            <LeftContent>
              <SectionTitle>
                {studentDetails?.name} Details
                {studentDetails?.status === 'ACTIVE' && (
                  <>
                    {' ('}
                    {studentDetails.currentCourseInfo?.grade}{' '}
                    {studentDetails.currentCourseInfo?.division}
                    {')'}
                  </>
                )}
              </SectionTitle>
              <ActiveContainer
                backgroundColor={
                  studentDetails?.status === 'ACTIVE'
                    ? pallet.lightGreen
                    : pallet.lightRed
                }
                color={
                  studentDetails?.status === 'ACTIVE'
                    ? pallet.green2
                    : pallet.red2
                }
              >
                {studentDetails?.status === 'ACTIVE' ? 'Active' : 'InActive'}
              </ActiveContainer>
              <ActiveContainer>
                {isNewAdmissionStudent ? 'New Admission' : 'Existing'}
              </ActiveContainer>
            </LeftContent>
            <>
              <StyledButton
                style={{ borderRadius: 8 }}
                aria-controls={isActionsOpen ? 'actions-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={isActionsOpen ? 'true' : undefined}
                onClick={handleOpenActions}
              >
                Actions
              </StyledButton>
              <ActionsMenu
                id='actions-menu'
                anchorEl={actionsAnchorEl}
                open={isActionsOpen}
                onClose={handleCloseActions}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {visibleActionItems.map((item) => (
                  <CustomMenuItem
                    key={item.key}
                    onClick={() => handleActionClick(item.key)}
                  >
                    {item.label}
                  </CustomMenuItem>
                ))}
              </ActionsMenu>
            </>
          </HeaderWrapper>
          <FormRenderer
            control={control}
            setValue={setValue}
            schema={studentProfileSchema}
          />
        </Box>
      </StudentProfileWrapper>

      <DialogModal
        open={isGenerateLcModalOpen}
        onClose={handleCloseGenerateLcModal}
        title='Please fill in the details to generate LC'
        maxWidth='sm'
      >
        <GenerateLcForm
          lcData={lcRequestData?.data}
          showToast={showToast}
          generateLC={generateLC}
        />
      </DialogModal>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleCloseConfirmationDialog}
        onConfirm={handleConfirmAction}
        message={confirmationDialogMessage}
      />
      {isBonafideLoading && <OverlayLoader show={isBonafideLoading} />}
    </Box>
  );
};

export default StudentProfile;
